import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";

/**
 * Concentration-time curves for the Analysis › Pharmacokinetics screen.
 *
 * One small-multiple chart per analyte (total antibody / intact ADC / free
 * payload). Within each, one line per dose level (mean concentration across
 * subjects at each nominal timepoint), colored consistently across analytes.
 * A shared semi-log toggle switches the y-axis between linear and log10 — the
 * standard way to read PK elimination curves.
 */

export interface PkRow {
  analyte?: string | null;
  timepoint?: string | null;
  concentration?: number | null;
  dose_level?: number | null;
  dose_unit?: string | null;
  unit?: string | null;
}

interface PkConcentrationChartProps {
  rows: PkRow[];
  isLoading: boolean;
}

// Dose level is ordinal, so this is a sequential single-hue ramp (Vivo blue,
// light → dark = low → high dose) rather than a categorical rainbow: the ordering
// is carried by lightness, and it stays on brand. Monotonic in lightness, with the
// palest steps trimmed so every line still reads against a white surface. The
// legend below the plot carries identity, since adjacent steps are close by design.
const DOSE_COLORS = [
  "#5FBEE3",
  "#3FAEDA",
  "#1E9DD1",
  "#0C8ABB",
  "#0A76A1",
  "#086086",
  "#064C68",
  "#05384D",
];

// Analytes displayed in PK-report order (fallback: whatever the data contains).
const ANALYTE_ORDER = [
  "Total antibody",
  "Intact ADC (conjugated)",
  "Free tirotecan payload",
];

const MARGIN = { top: 16, right: 20, bottom: 44, left: 56 };
const CHART_HEIGHT = 260;

/** Nominal hours post-dose for a PK timepoint label. Parses an explicit "N h"
 *  (incl. the "Day 8 (168 h)" form), with named fallbacks for the rest. */
function timepointHours(label: string | null | undefined): number | null {
  if (!label) return null;
  const text = String(label).trim();
  const paren = text.match(/\((\d+(?:\.\d+)?)\s*h\)/i);
  if (paren) return parseFloat(paren[1]);
  const bare = text.match(/^(\d+(?:\.\d+)?)\s*h$/i);
  if (bare) return parseFloat(bare[1]);
  const lower = text.toLowerCase();
  if (lower.includes("pre-dose") || lower.includes("predose")) return 0;
  if (lower.includes("end of infusion")) return 1;
  return null;
}

interface Series {
  key: string; // dose-level label, e.g. "0.8 mg/kg"
  doseValue: number;
  pts: { x: number; y: number }[];
}

function buildSeries(rows: PkRow[]): Series[] {
  // group rows by dose level
  const byDose = d3.group(rows, (d) =>
    d.dose_level == null ? "—" : String(d.dose_level),
  );
  const series: Series[] = [];
  for (const [doseKey, doseRows] of byDose) {
    const unit = doseRows.find((r) => r.dose_unit)?.dose_unit ?? "mg/kg";
    const doseValue = doseKey === "—" ? Number.NaN : parseFloat(doseKey);
    // mean concentration per nominal timepoint (hours)
    const byHour = d3.group(
      doseRows
        .map((r) => ({ h: timepointHours(r.timepoint), c: r.concentration }))
        .filter(
          (p): p is { h: number; c: number } =>
            p.h != null && p.c != null && !Number.isNaN(p.c),
        ),
      (p) => p.h,
    );
    const pts = [...byHour.entries()]
      .map(([h, group]) => ({ x: h, y: d3.mean(group, (p) => p.c) ?? 0 }))
      .sort((a, b) => a.x - b.x);
    if (pts.length) {
      series.push({
        key: doseKey === "—" ? "Unknown dose" : `${doseKey} ${unit}`,
        doseValue,
        pts,
      });
    }
  }
  // ascending dose so colors + legend read low→high
  series.sort((a, b) => (a.doseValue || 0) - (b.doseValue || 0));
  return series;
}

function AnalyteCurve({
  title,
  series,
  color,
  logScale,
  unitLabel,
}: {
  title: string;
  series: Series[];
  color: (key: string) => string;
  logScale: boolean;
  unitLabel: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setWidth(Math.max(entry.contentRect.width, 240));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const allX = series.flatMap((s) => s.pts.map((p) => p.x));
  const allY = series.flatMap((s) => s.pts.map((p) => p.y));
  const positiveY = allY.filter((y) => y > 0);

  const xScale = d3
    .scaleLinear()
    .domain([0, d3.max(allX) ?? 1])
    .range([0, innerW])
    .nice();

  const yScale = logScale
    ? d3
        .scaleLog()
        .domain([
          Math.max(d3.min(positiveY) ?? 0.001, 0.001) / 2,
          (d3.max(allY) ?? 1) * 1.2,
        ])
        .range([innerH, 0])
        .clamp(true)
    : d3
        .scaleLinear()
        .domain([0, (d3.max(allY) ?? 1) * 1.1])
        .range([innerH, 0])
        .nice();

  const lineGen = d3
    .line<{ x: number; y: number }>()
    .defined((p) => (logScale ? p.y > 0 : true))
    .x((p) => xScale(p.x))
    .y((p) => yScale(logScale ? Math.max(p.y, yScale.domain()[0]) : p.y))
    .curve(d3.curveMonotoneX);

  const xTicks = xScale.ticks(6);
  const yTicks = logScale
    ? yScale.ticks(4)
    : (yScale as d3.ScaleLinear<number, number>).ticks(5);

  return (
    <div className="mb-6">
      <p className="mb-1 text-sm font-semibold text-gray-700">{title}</p>
      <div ref={containerRef} className="w-full">
        <svg width={width} height={CHART_HEIGHT} className="block">
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {/* y gridlines */}
            {yTicks.map((t, i) => (
              <line
                key={`g${i}`}
                x1={0}
                x2={innerW}
                y1={yScale(t)}
                y2={yScale(t)}
                stroke="#eef2f7"
              />
            ))}
            {/* x axis */}
            <g transform={`translate(0,${innerH})`}>
              <line x1={0} x2={innerW} stroke="#9ca3af" />
              {xTicks.map((t, i) => (
                <g key={`x${i}`} transform={`translate(${xScale(t)},0)`}>
                  <line y2={4} stroke="#9ca3af" />
                  <text y={16} textAnchor="middle" fontSize={10} fill="#6b7280">
                    {t}
                  </text>
                </g>
              ))}
              <text
                x={innerW / 2}
                y={38}
                textAnchor="middle"
                fontSize={11}
                fill="#374151"
              >
                Nominal time post-dose (h)
              </text>
            </g>
            {/* y axis */}
            <g>
              <line y1={0} y2={innerH} stroke="#9ca3af" />
              {yTicks.map((t, i) => (
                <g key={`y${i}`} transform={`translate(0,${yScale(t)})`}>
                  <line x2={-4} stroke="#9ca3af" />
                  <text
                    x={-8}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fontSize={10}
                    fill="#6b7280"
                  >
                    {logScale ? d3.format("~g")(t) : t}
                  </text>
                </g>
              ))}
              <text
                transform={`translate(-44,${innerH / 2}) rotate(-90)`}
                textAnchor="middle"
                fontSize={11}
                fill="#374151"
              >
                {`Concentration${unitLabel ? ` (${unitLabel})` : ""}`}
              </text>
            </g>
            {/* series */}
            {series.map((s) => (
              <g key={s.key}>
                <path
                  d={lineGen(s.pts) ?? ""}
                  fill="none"
                  stroke={color(s.key)}
                  strokeWidth={2}
                />
                {s.pts
                  .filter((p) => (logScale ? p.y > 0 : true))
                  .map((p, i) => (
                    <circle
                      key={i}
                      cx={xScale(p.x)}
                      cy={yScale(
                        logScale ? Math.max(p.y, yScale.domain()[0]) : p.y,
                      )}
                      r={2.5}
                      fill={color(s.key)}
                    >
                      <title>{`${s.key} · ${p.x} h · ${d3.format(".3~g")(p.y)} ${unitLabel}`}</title>
                    </circle>
                  ))}
              </g>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
}

export default function PkConcentrationChart({
  rows,
  isLoading,
}: PkConcentrationChartProps) {
  const [logScale, setLogScale] = useState(false);

  const analytes = useMemo(() => {
    const present = Array.from(
      new Set(rows.map((r) => r.analyte).filter((a): a is string => !!a)),
    );
    const ordered = ANALYTE_ORDER.filter((a) => present.includes(a));
    const extra = present.filter((a) => !ANALYTE_ORDER.includes(a)).sort();
    return [...ordered, ...extra];
  }, [rows]);

  // Shared color scale over all dose levels so colors match across analytes.
  const { colorScale, legend, unitLabel } = useMemo(() => {
    const doseLabels = Array.from(
      new Map(
        rows
          .filter((r) => r.dose_level != null)
          .sort((a, b) => (a.dose_level ?? 0) - (b.dose_level ?? 0))
          .map((r) => [
            `${r.dose_level} ${r.dose_unit ?? "mg/kg"}`,
            r.dose_level as number,
          ]),
      ).keys(),
    );
    const cs = d3.scaleOrdinal<string>().domain(doseLabels).range(DOSE_COLORS);
    const unit = rows.find((r) => r.unit)?.unit ?? "";
    return { colorScale: cs, legend: doseLabels, unitLabel: unit };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-400">
        Loading concentration-time curves…
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-400">
        No pharmacokinetic concentration data for this trial.
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white shadow-omni p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-800">
            Concentration-time curves
          </h3>
          <p className="text-xs text-gray-500">
            Mean concentration by dose level, per analyte.
          </p>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={logScale}
            onChange={(e) => setLogScale(e.target.checked)}
            data-testid="pk-semilog-toggle"
          />
          Semi-log (log₁₀ y-axis)
        </label>
      </div>

      {/* Shared dose-level legend */}
      <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1">
        {legend.map((key) => (
          <span
            key={key}
            className="flex items-center gap-1 text-xs text-gray-600"
          >
            <span
              className="inline-block h-[3px] w-4 rounded"
              style={{ background: colorScale(key) }}
            />
            {key}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 xl:grid-cols-3">
        {analytes.map((analyte) => (
          <AnalyteCurve
            key={analyte}
            title={analyte}
            series={buildSeries(rows.filter((r) => r.analyte === analyte))}
            color={(key) => colorScale(key)}
            logScale={logScale}
            unitLabel={unitLabel}
          />
        ))}
      </div>
    </div>
  );
}
